import { createContext, type ReactNode, useContext } from 'react'
import ReactMarkdown, {
  type Components,
  defaultUrlTransform,
  type ExtraProps,
} from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { threadCopy } from '@/copy/thread'
import { CodeBlock } from '../CodeBlock/CodeBlock'
import { cx } from '../cx'
import { Link } from '../Link/Link'
import styles from './Markdown.module.css'
import { remarkPlainFootnotes, remarkThreadTokens } from './remarkThreadTokens'

type Element = NonNullable<ExtraProps['node']>
type ElementContent = Element['children'][number]

const SCHEME = /^([a-z][a-z0-9+.-]*):/i
const SAFE_SCHEME = /^(https?|mailto)$/i

/** react-markdown's default, narrowed to http(s), mailto and relative URLs. */
function safeUrl(url: string): string {
  const out = defaultUrlTransform(url)
  const scheme = SCHEME.exec(out)
  return !scheme || SAFE_SCHEME.test(scheme[1]) ? out : ''
}

/** Only an absolute http(s) URL on another origin opens in a new tab (D72). */
function isExternal(href: string): boolean {
  try {
    const url = new URL(href, window.location.href)
    return (
      /^https?:$/.test(url.protocol) && url.origin !== window.location.origin
    )
  } catch {
    return false
  }
}

function textOf(node: ElementContent): string {
  if (node.type === 'text') return node.value
  if (node.type === 'element') return node.children.map(textOf).join('')
  return ''
}

function languageOf(code: Element): string | undefined {
  const classes = code.properties?.className
  if (!Array.isArray(classes)) return undefined
  for (const name of classes) {
    if (typeof name === 'string' && name.startsWith('language-')) {
      return name.slice('language-'.length) || undefined
    }
  }
  return undefined
}

function Fence({ node }: { node?: Element }) {
  const code = node?.children.find(
    (child): child is Element =>
      child.type === 'element' && child.tagName === 'code',
  )
  if (!code) return null
  const text = code.children.map(textOf).join('').replace(/\n$/, '')
  return (
    <CodeBlock title={languageOf(code) ?? threadCopy.markdown.codeTitle}>
      {text}
    </CodeBlock>
  )
}

const MentionClick = createContext<((name: string) => void) | undefined>(
  undefined,
)

function Mention({ node, children }: { node?: Element; children?: ReactNode }) {
  const onMentionClick = useContext(MentionClick)
  const name = String(node?.properties?.dataMention ?? '')
  return onMentionClick ? (
    <button
      type="button"
      className={styles.mention}
      data-mention=""
      onClick={() => onMentionClick(name)}
    >
      {children}
    </button>
  ) : (
    <span className={styles.mention}>{children}</span>
  )
}

// Module-level, so each render (each streamed chunk) reconciles the same
// component types and the subtree keeps its focus and state.
const components: Components = {
  h1: ({ children }) => <h3>{children}</h3>,
  h2: ({ children }) => <h4>{children}</h4>,
  h3: ({ children }) => <h5>{children}</h5>,
  h4: ({ children }) => <h6>{children}</h6>,
  h5: ({ children }) => <h6>{children}</h6>,
  h6: ({ children }) => <h6>{children}</h6>,
  a: ({ href, children }) =>
    href ? (
      <Link href={href} external={isExternal(href)}>
        {children}
      </Link>
    ) : (
      <span>{children}</span>
    ),
  img: ({ src, alt }) =>
    typeof src === 'string' && src ? (
      <Link href={src} external={isExternal(src)}>
        {alt || threadCopy.markdown.imageFallback}
      </Link>
    ) : null,
  pre: ({ node }) => <Fence node={node} />,
  code: ({ children }) => <code className={styles.inlineCode}>{children}</code>,
  table: ({ children }) => (
    <div className={styles.tableScroll}>
      <table className={styles.table}>{children}</table>
    </div>
  ),
  button: Mention,
}

const remarkPlugins = [remarkGfm, remarkPlainFootnotes, remarkThreadTokens]

/** Markdown from an untrusted producer: no raw HTML, no unsafe URLs, no images. */
export function Markdown({
  source,
  onMentionClick,
  streaming,
  ground = 'page',
  className,
}: {
  source: string
  onMentionClick?: (name: string) => void
  streaming?: boolean
  /** `surface` (inside a card) lifts inline code to raise. */
  ground?: 'page' | 'surface'
  className?: string
}) {
  return (
    <div
      className={cx(styles.markdown, className)}
      data-streaming={streaming ? '' : undefined}
      data-ground={ground}
    >
      <MentionClick.Provider value={onMentionClick}>
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          components={components}
          urlTransform={safeUrl}
        >
          {source}
        </ReactMarkdown>
      </MentionClick.Provider>
    </div>
  )
}
